import type { PaymentMethod } from './PaymentMethod';
import type { PaymentSource } from './Payment';

/**
 * The tenant's prepaid wallet. Contract: docs/api-types/wallet.md (tenant surface) and
 * docs/api-types/admin-wallets.md (staff surface). Money is a 2-decimal **string** end-to-end —
 * parseFloat only to format or compare for display gating, never to store or send back.
 */
export interface Wallet {
  /** Current prepaid balance — may be negative after a Mollie refund/chargeback on a spent top-up. */
  balance: string;
  currency: string;
  /** Minimum top-up (`10.00`). */
  deposit_min: string;
  /** Maximum top-up (`5000.00`). */
  deposit_max: string;
}

export type WalletTransactionKind = 'deposit' | 'charge' | 'reversal' | 'clawback' | 'adjustment';

export type WalletReferenceType =
  | 'wallet_deposit'
  | 'facturier_invoice'
  | 'domain_order'
  | 'wallet_transaction'
  | 'staff'
  // A GPU server hour (`kind: 'charge'`, `reference_label` = the server hostname).
  | 'gpu_server_usage';

/** One ledger row — the customer's statement line. Newest first on every list. */
export interface WalletTransaction {
  id: number;
  kind: WalletTransactionKind;
  /** **Signed** 2-decimal string: credit `+`, debit `-`. */
  amount: string;
  balance_after: string;
  reference_type: WalletReferenceType | string | null;
  reference_id: string | null;
  /** The human key: an invoice `numero`, the order's first domain, the Mollie `tr_…` of a top-up. */
  reference_label: string | null;
  description: string;
  created_at: string | null;
}

export type WalletDepositSource = 'checkout' | 'card';

/** `created` until the Mollie payment exists, then the Mollie status mirror. */
export type WalletDepositStatus =
  | 'created'
  | 'open'
  | 'pending'
  | 'authorized'
  | 'paid'
  | 'failed'
  | 'expired'
  | 'canceled';

/** A top-up. `credited` flips once (status is then `paid`). */
export interface WalletDeposit {
  id: number;
  amount: string;
  currency: string;
  source: WalletDepositSource;
  status: WalletDepositStatus;
  credited: boolean;
  credited_at: string | null;
  created_at: string | null;
}

/** Terminal deposit statuses that will never credit — the return page shows a failure for these. */
export const WALLET_DEPOSIT_FAILED_STATUSES: ReadonlyArray<WalletDepositStatus> = ['failed', 'expired', 'canceled'];

/** `data` of `GET /clients/wallet`. */
export interface WalletShow {
  wallet: Wallet;
  pending_deposits: WalletDeposit[];
  default_payment_method: PaymentMethod | null;
}

/** Body of `POST /clients/wallet/deposits`. `wallet` is refused as a source (a top-up cannot pay itself). */
export interface WalletDepositCreateRequest {
  amount: string;
  source?: WalletDepositSource;
  payment_method_id?: number;
  save_card?: boolean;
}

/** `data` of `POST /clients/wallet/deposits`. `checkout_url` is `null` for a saved-card top-up. */
export interface WalletDepositCreateResult {
  deposit_id: number;
  checkout_url: string | null;
  payment_id: string | null;
  status: WalletDepositStatus;
  credited: boolean;
  source: WalletDepositSource;
}

/** `data` of `GET /clients/wallet/deposits/{id}` — the return page's poll. */
export interface WalletDepositShow {
  deposit: WalletDeposit;
  balance: string;
}

// ---------------------------------------------------------------------------------------------
// Staff surface — docs/api-types/admin-wallets.md
// ---------------------------------------------------------------------------------------------

/** One row of `GET /admin/wallets[?at=YYYY-MM-DD]`. With `at`, `balance` is the end-of-day balance. */
export interface AdminWalletRow {
  client_id: number;
  client_name: string;
  balance: string;
  currency: string;
  updated_at: string | null;
}

/** Ledger row as the staff endpoints return it — the tenant row plus who/which client. */
export interface AdminWalletTransaction extends WalletTransaction {
  client_id: number;
  /** Present on the cross-client export only. */
  client_name?: string;
  user_id: number | null;
}

/** Deposit as the staff detail returns it — the tenant object plus the Mollie id and any clawback. */
export interface AdminWalletDeposit extends WalletDeposit {
  mollie_payment_id: string | null;
  /**
   * The refunds and chargebacks already taken back from the wallet for this top-up (staff-recorded
   * refunds included). A `credited` top-up with a `mollie_payment_id` can be refunded up to
   * `amount − clawback_amount`; the refund form echoes the figure it DISPLAYED as `expected_clawback_amount`.
   */
  clawback_amount: string;
}

export type AdminWalletInvoicePaymentStatus = 'settling' | 'paid' | 'reversed' | 'needs_reconciliation';

/**
 * A settlement attempt on a Facturier invoice (staff detail) — a wallet debit, or (since 0.85.0) a
 * saved-card charge. `source`/`mollie_payment_id` are optional until the admin-wallets.md field
 * table documents them: an UNLINKED saved-card claim is `source: 'card'` with no Mollie payment id
 * (the create response was lost) and is resolvable at any age.
 */
export interface AdminWalletInvoicePayment {
  id: number;
  source?: 'wallet' | 'card';
  mollie_payment_id?: string | null;
  facturier_invoice_id: number;
  /** VARCHAR snapshot of the Facturier numero (e.g. `"2026-0123"`) — always present. */
  invoice_numero: string;
  amount: string;
  status: AdminWalletInvoicePaymentStatus;
  wallet_transaction_id: number | null;
  facturier_marked_paid_at: string | null;
  created_at: string | null;
}

/**
 * `data` of `GET /admin/wallets/{clientId}`. Answers for any existing organization, with or without a
 * wallet row (balance `0.00` and empty lists then); only an unknown id is "Organization not found".
 */
export interface AdminWalletShow {
  client: { id: number; name: string };
  /** Balance and currency only — the top-up bounds come with the tenant `GET /clients/wallet`, not here. */
  wallet: Pick<Wallet, 'balance' | 'currency'>;
  transactions: AdminWalletTransaction[];
  deposits: AdminWalletDeposit[];
  invoice_payments: AdminWalletInvoicePayment[];
}

/** Body of `POST /admin/wallets/{clientId}/adjustments`. `amount` is **signed** (`+` credit, `-` debit). */
export interface AdminWalletAdjustmentRequest {
  amount: string;
  description: string;
  allow_negative?: boolean;
}

/** `data` of `POST /admin/wallets/{clientId}/adjustments`. */
export interface AdminWalletAdjustmentResult {
  transaction: AdminWalletTransaction;
  balance: string;
}

/**
 * Body of `POST /admin/wallets/{clientId}/deposits/{depositId}/refund` — record the refund of (part of) a
 * credited top-up BEFORE making it at Mollie. `expected_clawback_amount` is the `clawback_amount` the page
 * displayed: a second submission carries the old figure and is refused.
 */
export interface AdminWalletDepositRefundRequest {
  /** Positive, at most 2 decimals, at most `amount − clawback_amount`. */
  amount: string;
  expected_clawback_amount: string;
  /** The reason (1..255 chars) — the statement line the tenant sees. */
  description: string;
  /** Let the debit push the balance below zero (record a receivable). */
  allow_negative: boolean;
}

/** `data` of `POST /admin/wallets/{clientId}/deposits/{depositId}/refund`. */
export interface AdminWalletDepositRefundResult {
  transaction: AdminWalletTransaction;
  balance: string;
  deposit: {
    id: number;
    amount: string;
    clawback_amount: string;
    refundable_remaining: string;
    mollie_payment_id: string;
  };
  /** `"Refund <amount> EUR at Mollie on payment <tr_…>"` — the second half of the procedure, by hand. */
  next_step: string;
}

/**
 * Body of `POST /admin/wallets/{clientId}/deposits/{depositId}/refund/cancel` — the inverse of a recorded
 * refund whose money cannot go back through Mollie (never made, recorded by mistake, failed or canceled
 * there). No `allow_negative`: it only credits. Same double-submit guard as the refund.
 */
export interface AdminWalletDepositRefundCancelRequest {
  /** Positive, at most 2 decimals, at most `clawback_amount − floor` (the floor is only known to the backend). */
  amount: string;
  expected_clawback_amount: string;
  /** The reason (1..255 chars) — the statement line the tenant sees. */
  description: string;
}

/** `data` of `POST /admin/wallets/{clientId}/deposits/{depositId}/refund/cancel` (a POSITIVE `clawback` row). */
export interface AdminWalletDepositRefundCancelResult {
  transaction: AdminWalletTransaction;
  balance: string;
  deposit: AdminWalletDepositRefundResult['deposit'];
}

/** `data` of `POST /admin/wallets/{clientId}/invoice-payments/{id}/resolve`. */
export interface AdminWalletResolveResult {
  /**
   * `paid` (settled) · `reversed` (charge reversed, claim released) · `released` (an unlinked
   * saved-card claim older than 10 min with no payment at Mollie) · `pending` (a young unlinked
   * saved-card claim — nothing decided yet).
   */
  outcome: 'paid' | 'reversed' | 'released' | 'pending';
  invoice_payment_id: number;
}

/** Filters of `GET /admin/wallets/transactions` (the accountant's period export, capped at 5000 rows). */
export interface AdminWalletTransactionsQuery {
  from?: string;
  to?: string;
  client_id?: number;
  kind?: WalletTransactionKind;
}

/** `data` of `GET /admin/wallets/transactions`. */
export interface AdminWalletTransactionsShow {
  transactions: AdminWalletTransaction[];
  from: string | null;
  to: string | null;
  truncated: boolean;
}

/**
 * `GET|PUT /clients/renewal-autopay` — the organization's opt-in to having its domain renewal
 * invoices paid automatically (wallet.md, "Automatic payment of domain renewals"). The daily renewal
 * workflow pays each still-unpaid renewal invoice from expiry − 7 days: from the prepaid balance when
 * it covers the whole invoice, otherwise with the default saved card. `PUT` is refused while
 * impersonating — consenting to unattended charges is the organization's own decision.
 */
export interface RenewalAutopay {
  /** The opt-in. Default `false`. */
  enabled: boolean;
  /** ISO-8601 — when it last changed; `null` = never. */
  changed_at: string | null;
  /** A valid, unexpired saved card exists. Without one, autopay relies on the balance alone. */
  has_saved_card: boolean;
  /** The current balance, 2-decimal string. */
  wallet_balance: string;
  currency: string;
}

/** `PUT /clients/renewal-autopay` body — `enabled` must be a real JSON boolean. */
export type RenewalAutopayUpdateRequest = Pick<RenewalAutopay, 'enabled'>;

/** One language of the invoice-autopay terms (`terms.fr` / `terms.en`). */
export interface InvoiceAutopayTermsText {
  /** What turning automatic payment on authorises, in order. */
  points: string[];
  /** The unpaid-invoice warning — shown on its own, never buried in the points. */
  warning: { title: string; text: string };
}

/** The current terms of the invoice autopay opt-in — the backend holds the one copy. */
export interface InvoiceAutopayTerms {
  /** The current terms version (e.g. `2026-10-23`) — what `PUT` must echo to turn the opt-in on. */
  version: string;
  fr: InvoiceAutopayTermsText;
  en: InvoiceAutopayTermsText;
}

/**
 * `GET|PUT /clients/invoice-autopay` — the organization's opt-in to having ALL its outstanding invoices
 * paid automatically, separate from {@link RenewalAutopay} (wallet.md, "Automatic payment of all invoices").
 */
export interface InvoiceAutopay {
  /** The opt-in. Default `false`. */
  enabled: boolean;
  /** The terms version accepted when it was turned on; `null` while off. Differs from `terms.version` ⇒ newer terms. */
  accepted_terms_version: string | null;
  /** ISO-8601 — when it last changed; `null` = never. */
  changed_at: string | null;
  /** A valid, unexpired saved card exists. Without one, autopay relies on the balance alone. */
  has_saved_card: boolean;
  /** The current balance, 2-decimal string. */
  wallet_balance: string;
  currency: string;
  /** The current terms — always present, on or off. */
  terms: InvoiceAutopayTerms;
}

/**
 * `PUT /clients/invoice-autopay` body. Turning on must echo the `terms.version` the user was shown;
 * turning off sends `enabled` alone.
 */
export type InvoiceAutopayUpdateRequest = { enabled: true; accepted_terms_version: string } | { enabled: false };

// ── Staff "charge now" on the invoice autopay consent (docs/api-types/admin-invoice-autopay.md) ──

/** What a charge would do today for one unpaid invoice (`GET /admin/invoice-autopay/{clientId}`). */
export type AdminInvoiceAutopayPlan = 'wallet' | 'card' | 'declined' | 'no_funds' | 'busy' | 'refused';

/** Why the Manager must not collect an invoice (`plan: 'refused'`); `message` is the tenant pay()'s sentence. */
export interface AdminInvoiceAutopayRefusal {
  code: 'already_paid' | 'not_payable' | 'being_reconciled' | 'nothing_to_pay' | 'unreadable';
  message: string;
}

/** One unpaid `officielle` invoice of the preview (oldest first). */
export interface AdminInvoiceAutopayInvoice {
  /** Facturier invoice id — the `{invoiceId}` of the charge route. */
  invoice_id: number;
  numero: string | null;
  /** `YYYY-MM-DD`. */
  date: string | null;
  intitule: string | null;
  /** Total incl. VAT. */
  tvac: string | null;
  /** What a charge would collect (the outstanding balance); `null` when `plan` is `busy` or `refused`. */
  amount: string | null;
  plan: AdminInvoiceAutopayPlan;
  /** Set when `plan` is `refused`. */
  refusal: AdminInvoiceAutopayRefusal | null;
}

/** The organization's own consent, as staff see it. */
export interface AdminInvoiceAutopayConsent {
  /** `false` ⇒ every charge is refused. */
  enabled: boolean;
  /** The terms version the organization accepted; `null` while off. */
  accepted_terms_version: string | null;
  current_terms_version: string;
  /** `enabled` and the accepted version is the current one — informational, a charge is not gated on it. */
  terms_current: boolean;
  /** ISO-8601 — when the opt-in last changed. */
  changed_at: string | null;
  /** `name` / `email` are `null` when that user no longer exists. */
  changed_by: { id: number; name: string | null; email: string | null } | null;
}

export interface AdminInvoiceAutopaySummary {
  invoices: number;
  /** Rows planned `wallet` or `card`. */
  chargeable: number;
  total: string;
  from_wallet: string;
  by_card: string;
  /** The balance once the `wallet` rows are paid. */
  wallet_after: string;
}

/** `data` (flat) of `GET /admin/invoice-autopay/{clientId}`. */
export interface AdminInvoiceAutopayPreview {
  client: { id: number; name: string };
  invoice_autopay: AdminInvoiceAutopayConsent;
  wallet: { balance: string; currency: string };
  /**
   * The card a charge would use (display data only); `null` = none. The backend copies the saved-card
   * columns, which are nullable (as in `PaymentMethod`), hence `string | null`.
   */
  card: { card_label: string | null; card_last4: string | null; card_expiry: string | null } | null;
  invoices: AdminInvoiceAutopayInvoice[];
  summary: AdminInvoiceAutopaySummary;
  /** More than 500 unpaid invoices: only the 500 newest were read. */
  truncated: boolean;
}

/** The result of one staff charge. */
export type AutopayOutcome =
  | 'paid'
  | 'in_flight'
  | 'declined'
  | 'no_funds'
  | 'busy'
  | 'refused'
  | 'not_consented'
  | 'failed';

/** `data` (flat) of `POST /admin/invoice-autopay/{clientId}/invoices/{invoiceId}/charge`. */
export interface AdminInvoiceAutopayChargeResult {
  invoice_id: number;
  outcome: AutopayOutcome;
  /** A fixed staff-readable sentence for the outcome — shown verbatim. */
  message: string;
  /** The balance after the charge. */
  wallet: { balance: string; currency: string };
}

// Re-exported for callers that only import from Wallet.ts.
export type { PaymentSource };
